import { useEffect, useState, useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {ArrowLeft, ChevronLeft, ChevronRight, Car, Calendar} from 'lucide-react'
import { useVehicles } from '@/hooks/useVehicles'

interface Vehicle {
  _id: string
  name: string
  brand?: string
  model?: string
  category?: string
  imageUrl?: string
  images?: string[]
}

export default function VehiclePhotosPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { vehicles, fetchVehicles } = useVehicles()

  const fromParam = searchParams.get('from')
  const handleBack = () => {
    if (fromParam) {
      navigate(fromParam)
    } else {
      navigate('/flotte')
    }
  }

  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState<number>(() => {
    const raw = Number(searchParams.get('photo'))
    return Number.isFinite(raw) && raw >= 0 ? raw : 0
  })

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    fetchVehicles()
  }, [])

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }
    if (!vehicles || vehicles.length === 0) return
    const found = vehicles.find((v: any) => v._id === id) || null
    setVehicle(found)
    setLoading(false)
    console.log('VehiclePhotosPage: vehicle found:', found ? found._id : 'NOT FOUND', 'for id:', id)
  }, [id, vehicles])

  const allImages = useMemo(() => {
    if (!vehicle) return []
    const list: string[] = []
    if (vehicle.imageUrl) list.push(vehicle.imageUrl)
    if (vehicle.images?.length) {
      vehicle.images.forEach((img) => {
        if (img && img !== vehicle.imageUrl) list.push(img)
      })
    }
    return list
  }, [vehicle])

  const totalImages = allImages.length
  const hasMultiple = totalImages > 1

  useEffect(() => {
    if (currentIndex >= totalImages && totalImages > 0) {
      setCurrentIndex(0)
    }
  }, [currentIndex, totalImages])

  const goTo = (index: number) => {
    if (totalImages === 0) return
    if (index < 0) setCurrentIndex(totalImages - 1)
    else if (index >= totalImages) setCurrentIndex(0)
    else setCurrentIndex(index)
  }

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!hasMultiple) return
      if (e.key === 'ArrowLeft') goTo(currentIndex - 1)
      else if (e.key === 'ArrowRight') goTo(currentIndex + 1)
      else if (e.key === 'Escape') handleBack()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [currentIndex, hasMultiple, totalImages, fromParam])

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-gradient-to-br from-gray-900 via-gray-950 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-[#fbbf24] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-300 font-semibold">Chargement des photos...</p>
        </div>
      </div>
    )
  }

  if (!vehicle) {
    return (
      <div className="min-h-[70vh] bg-gradient-to-br from-gray-900 via-gray-950 to-black flex items-center justify-center px-4">
        <div className="text-center bg-gray-800/50 backdrop-blur-sm p-10 rounded-2xl border border-gray-700 max-w-md">
          <Car className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-white mb-2">Véhicule introuvable</h2>
          <p className="text-gray-400 mb-6">Ce véhicule n'existe pas ou a été retiré.</p>
          <button
            onClick={() => navigate('/flotte')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold px-6 py-3 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/40 transition-all hover:scale-105"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à la flotte
          </button>
        </div>
      </div>
    )
  }

  const vehicleTitle = vehicle.brand && vehicle.model
    ? `${vehicle.brand} ${vehicle.model}`
    : vehicle.name

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-950 to-black">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-gray-900/80 backdrop-blur-xl border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-white hover:text-[#fbbf24] font-semibold transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="hidden sm:inline">Retour</span>
          </button>

          <div className="flex-1 text-center min-w-0">
            <h1 className="text-lg sm:text-2xl font-black text-white truncate">
              {vehicleTitle}
            </h1>
            {vehicle.category && (
              <p className="text-xs sm:text-sm text-[#fbbf24] font-semibold uppercase tracking-wider">
                {vehicle.category}
              </p>
            )}
          </div>

          <button
            onClick={() => navigate(`/reserver?vehicleId=${vehicle._id}`)}
            className="hidden sm:inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold px-4 py-2 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/40 transition-all hover:scale-105"
          >
            <Calendar className="w-4 h-4" />
            Réserver
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {totalImages === 0 ? (
          <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-16 text-center">
            <Car className="w-20 h-20 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-300 font-semibold">Aucune photo disponible pour ce véhicule.</p>
          </div>
        ) : (
          <>
            {/* Photo principale agrandie */}
            <div className="relative bg-black rounded-2xl overflow-hidden border border-gray-700 shadow-2xl">
              <div className="relative aspect-[16/10] sm:aspect-[16/9] flex items-center justify-center bg-black">
                <img
                  src={allImages[currentIndex]}
                  alt={`${vehicleTitle} - Photo ${currentIndex + 1}`}
                  className="w-full h-full object-contain"
                  draggable={false}
                />

                {/* Flèches */}
                {hasMultiple && (
                  <>
                    <button
                      onClick={() => goTo(currentIndex - 1)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-[#fbbf24] hover:text-black text-white p-3 rounded-full transition-all backdrop-blur-sm shadow-lg hover:scale-110"
                      aria-label="Photo précédente"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => goTo(currentIndex + 1)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-[#fbbf24] hover:text-black text-white p-3 rounded-full transition-all backdrop-blur-sm shadow-lg hover:scale-110"
                      aria-label="Photo suivante"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Compteur */}
                {hasMultiple && (
                  <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-md text-white text-sm font-bold px-4 py-2 rounded-full border border-white/10">
                    {currentIndex + 1} / {totalImages}
                  </div>
                )}
              </div>
            </div>

            {/* Liste des miniatures */}
            {hasMultiple && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider">
                    Toutes les photos ({totalImages})
                  </h2>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {allImages.map((img, idx) => (
                    <button
                      key={`${img}-${idx}`}
                      onClick={() => goTo(idx)}
                      className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-105 ${
                        idx === currentIndex
                          ? 'border-[#fbbf24] shadow-lg shadow-yellow-400/30 ring-2 ring-[#fbbf24]/40'
                          : 'border-gray-700 opacity-70 hover:opacity-100 hover:border-gray-500'
                      }`}
                      aria-label={`Voir photo ${idx + 1}`}
                    >
                      <img
                        src={img}
                        alt={`${vehicleTitle} miniature ${idx + 1}`}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                      {idx === currentIndex && (
                        <div className="absolute inset-0 bg-[#fbbf24]/10 pointer-events-none" />
                      )}
                      <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        {idx + 1}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA mobile */}
            <div className="mt-8 sm:hidden">
              <button
                onClick={() => navigate(`/reserver?vehicleId=${vehicle._id}`)}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold px-6 py-4 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/40 transition-all"
              >
                <Calendar className="w-5 h-5" />
                Réserver ce véhicule
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
